import { AppConfig } from '@core/domain/AppConfig.ts';
import {
  getEmbeddedSnapshotRevision,
  isSnapshotRevisionUpgradePending,
  readStoredSnapshotRevision,
} from '@src/modules/platform/local-sqlite/infrastructure/snapshotRevisionGuard.ts';
import { probeLocalSqliteOpfsCapability } from '@src/modules/platform/local-sqlite/infrastructure/localSqliteOpfsCapability.ts';
import { isLocalSqlitePeerSyncSupported } from '@src/modules/platform/local-sqlite/infrastructure/localSqlitePeerSync.ts';
import { EMPTY_LOCAL_CATALOG_COUNTS } from '@src/modules/kernel/LocalCatalogCounts.ts';
import { queryLocalCatalogCounts } from '@src/modules/platform/local-sqlite/infrastructure/localCatalogCounts.ts';
import type { GetSqlJsDatabase } from '@src/modules/platform/local-sqlite/infrastructure/sqlJsRowHelpers.ts';
import type { SystemDiagnosticsPort, SystemHealthSnapshot } from '../../domain/ports/SystemDiagnosticsPort.ts';
import { queryLocalStorageDiagnostics } from './localStorageDiagnostics.ts';
import { countOrphanMediaBlobs, purgeOrphanMediaBlobs } from './orphanMediaBlobs.ts';
import { detectAppMaintenancePlatform } from './PwaAppMaintenanceAdapter.ts';

export class BrowserSystemDiagnosticsAdapter implements SystemDiagnosticsPort {
  constructor(
    private readonly sessionPresent: () => boolean,
    private readonly remoteSyncAvailable: boolean,
    private readonly getDb?: GetSqlJsDatabase,
  ) {}

  async collectHealth(_remoteSyncAvailable: boolean): Promise<SystemHealthSnapshot> {
    const platform = detectAppMaintenancePlatform();
    const sw = globalThis.navigator?.serviceWorker;
    let registered = false;
    let controlling = false;
    let scriptUrl: string | null = null;
    if (sw) {
      try {
        const reg = await sw.getRegistration();
        registered = reg != null;
        scriptUrl = reg?.active?.scriptURL ?? reg?.installing?.scriptURL ?? reg?.waiting?.scriptURL ?? null;
        controlling = sw.controller != null;
      } catch {
        /* ignore */
      }
    }

    let cacheNames: string[] = [];
    try {
      if (globalThis.caches) cacheNames = await caches.keys();
    } catch {
      /* ignore */
    }

    const storeUrl = (import.meta.env.ADMIN_MOBILE_STORE_URL ?? '').trim() || null;
    const opfs = await probeLocalSqliteOpfsCapability();
    const storage = await queryLocalStorageDiagnostics(this.getDb);

    return {
      buildRevision: (import.meta.env.ADMIN_BUILD_REVISION ?? '').trim() || '—',
      snapshotRevision: getEmbeddedSnapshotRevision(),
      platform,
      online: globalThis.navigator?.onLine !== false,
      sessionPresent: this.sessionPresent(),
      telemetryProvider: (import.meta.env.TELEMETRY_PROVIDER ?? 'noop').trim() || 'noop',
      serviceWorker: {
        supported: sw != null,
        registered,
        controlling,
        scriptUrl,
        cacheNames,
      },
      sqlite: {
        idbName: AppConfig.getLocalSqliteIdbName(),
        storedRevision: readStoredSnapshotRevision(),
        embeddedRevision: getEmbeddedSnapshotRevision(),
        upgradePending: isSnapshotRevisionUpgradePending(),
        peerSyncSupported: isLocalSqlitePeerSyncSupported(),
        opfsAvailable: opfs.supported,
        opfsReason: opfs.reason,
      },
      storage,
      sync: {
        lastPullAt: null,
        lastError: null,
        remoteAvailable: this.remoteSyncAvailable,
      },
      catalogs: this.getDb ? queryLocalCatalogCounts(this.getDb) : { ...EMPTY_LOCAL_CATALOG_COUNTS },
      mobileStoreUrl: storeUrl,
      resilience: {
        lastPushSuccessAt: null,
        lastExportAt: null,
      },
    };
  }

  copyDiagnosticsJson(snapshot: SystemHealthSnapshot): string {
    return JSON.stringify(snapshot, null, 2);
  }

  async countOrphanMediaBlobs(): Promise<number> {
    if (!this.getDb) return 0;
    return countOrphanMediaBlobs(this.getDb);
  }

  async purgeOrphanMediaBlobs(): Promise<number> {
    if (!this.getDb) return 0;
    return purgeOrphanMediaBlobs(this.getDb);
  }
}
